import { AxiosError } from 'axios'
import { REDUX } from 'constants/'
import { notificationStatus } from 'constants/notificationStatus'
import { Dispatch } from 'redux'
import ReportService from 'services/Report'
import { ReportAction } from 'types/report'

export interface IReportProps {
    dispatch: Dispatch<ReportAction>
    parameter?: string,
    countType?: CountType,
    action?: string,
    errorAction?: string | null,
    locations?: string | null,
    reporterSector?: string | null,
    startDate?: string | null,
    finalDate?: string | null,
    damageLevel?: string | null,
    incidentType?: string | null,
    omsClassification?: string | null
    bringArchived?: boolean | null
    status?: string | null
    sentinelEvent?: string | null
    imageRisk?: string | null
    multipleFactor?: string | null
    helperList?: any[]
    dashboards?: string | null
    sectorActive?: string | null
    subState?: string | null
    uUserType?: string | null
}

export type CountType = 'length' | 'sum' | 'status' | 'countMonth' | 'birdCount' | 'locationCount' | 'sectorCount' | 'omsCount' | 'topIncident' | 'value' | 'subDamageLevel' | 'multiplierStats'

const mapNotifications = (data: any[]) => {
    return data?.map(n => {
        return (
            {
                field: n.value,
                value: n.count
            }
        )
    }) ?? []
}

export const getReport = async ({
    dispatch,
    action,
    errorAction,
    countType,
    parameter = null,
    locations = null,
    reporterSector = null,
    startDate = null,
    finalDate = null,
    damageLevel = null,
    incidentType = null,
    omsClassification = null,
    helperList,
    bringArchived = null,
    status = null,
    sentinelEvent = null,
    imageRisk = null,
    sectorActive = null,
    subState = null,
    uUserType = null,
}: IReportProps) => {
    const getLength = (data: any) => {
        return data?.length
    }
    const getSum = (data: any) => {
        return data?.map(d => d.count).reduce((acumulador, elemento) => acumulador + elemento, 0);
    }
    const getByStatus = (data: any, finalizedBySubStatus?: any) => {
        // Extrai dados de substatus do array (com_tratativa e sem_tratativa)
        const comTratativaItem = data?.find((item: any) => item.value === 'com_tratativa')
        const semTratativaItem = data?.find((item: any) => item.value === 'sem_tratativa')
        
        // Substatus não entram na soma de u_state (evita contar de novo no total)
        const subStatusValues = ['com_tratativa', 'sem_tratativa', 'substatus_null']
        const statusData = data?.filter((item: any) => 
            !subStatusValues.includes(item.value)
        )
        
        // Sempre monta finalizedBySubStatus para que a resposta atual substitua a contagem anterior no reducer
        let subStatusData = finalizedBySubStatus
        if (!subStatusData) {
            subStatusData = {
                withTreatment: comTratativaItem ? Number(comTratativaItem.count) : 0,
                withoutTreatment: semTratativaItem ? Number(semTratativaItem.count) : 0
            }
        }
        
        let countNotifications = { 'total': { count: getSum(statusData || data), label: 'total', value: '7' } }
        for (let state of notificationStatus) {
            const label = state?.label
            const value = state?.value
            let findItem = (statusData || data)?.filter((s: any) => s.value === state.value)
            let count = 0;
            if (findItem?.length > 0) {
                count = Number(findItem[0].count)
            }
            countNotifications[label] = {
                label: label,
                count: count,
                value: value
            }
        }
        // Finalizadas = soma dos substatus retornados (com_tratativa + sem_tratativa)
        const finalizedTotal =
            Number(subStatusData?.withTreatment || 0) +
            Number(subStatusData?.withoutTreatment || 0)
        if (countNotifications['Finalizada']) {
            countNotifications['Finalizada'].count = finalizedTotal
        }
        (countNotifications as any).finalizedBySubStatus = subStatusData
        return countNotifications;
    }
    const countMonth = (data: any) => {
        return (data ?? [])
            .map(n => {
                const month = new Date(n.value)
                if (Number.isNaN(month.getTime())) {
                    return null
                }
                const str = month.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' }).split(' de ')[0]
                return (
                    {
                        month,
                        field: str.charAt(0).toUpperCase() + str.slice(1),
                        value: n.count
                    }
                )
            })
            .filter(Boolean)
            .sort((d1, d2) => d1.month.getTime() - d2.month.getTime())
            .map(n => {
                return ({
                    field: n.field,
                    value: n.value
                })
            })
    }
    const birdGraphCount = (data: any, helperList?: any[]) => {
        const damageNotifications = mapNotifications(data)
        return helperList?.map(d => {
            let values = damageNotifications?.filter(n => n.field === d.value)
            let value = values?.length ? values[0].value : 0
            return (
                {
                    index: d?.value,
                    field: d?.label,
                    value
                }
            )
        }).slice(1);
    }
    const locationCount = (data: any, helperList?: any[]) => {
        const locationNotifications = mapNotifications(data)
        return helperList?.map(d => {
            let values = locationNotifications?.filter(n => n.field === d.sys_id)
            let value = values?.length ? values[0].value : 0
            return (
                {
                    field: d?.name,
                    value
                }
            )
        }).filter(d => d.value > 0 && d.field !== '');
    }
    const sectorCount = (data: any, helperList?: any[]) => {
        const sectorNotifications = mapNotifications(data)
        
        // Mapear setores do helperList
        const mappedFromHelperList = helperList?.map(d => {
            let values = sectorNotifications.filter(n => n.field === d.sys_id)
            let value = values?.length ? values[0].value : 0
            return {
                field: d?.u_name,
                value,
                sys_id: d.sys_id
            }
        }).filter(d => d.value > 0 && d.field !== '') || []
        
        // Encontrar setores retornados pelo backend que não estão no helperList
        const helperListSysIds = new Set(helperList?.map(d => d.sys_id) || [])
        const missingSectors = sectorNotifications
            .filter(n => !helperListSysIds.has(n.field) && n.value > 0)
            .map(n => ({
                field: n.field,
                value: n.value,
                sys_id: n.field
            }))
        
        // Combinar ambos os arrays
        return [...mappedFromHelperList, ...missingSectors]
    }
    const omsCount = (data: any, helperList?: any[]) => {
        const omsNotifications = mapNotifications(data)
        return helperList?.map(d => {
            let values = omsNotifications?.filter(n => n.field === d.value)
            let value = values?.length ? values[0].value : 0
            return (
                {
                    field: d?.label,
                    value
                }
            )
        }).slice(1).sort((d1, d2) => d1.value - d2.value)
    }
    const incidentTypeCount = (data: any, helperList?: any[]) => {
        const incidentNotifications = mapNotifications(data)
        let newData = helperList?.map(d => {
            let values = incidentNotifications?.filter(n => n.field === d.value)
            let value = values?.length ? values[0].value : 0
            return (
                {
                    field: d?.label,
                    value
                }
            )
        }).slice(1).filter(item => item.value > 0).sort((item1, item2) => item1.value - item2.value)
        let totalItens = data?.length??0;
        if (totalItens > 0) {
            newData = newData.slice(totalItens - 10, totalItens)
        }
        return newData
    }

    const getCountType = (key: CountType, data: any, helperList?: any[], finalizedBySubStatus?: any) => {
        switch (key) {
            case 'length':
                return getLength(data)
            case 'sum':
                return getSum(data)
            case 'status':
                return getByStatus(data, finalizedBySubStatus)
            case 'countMonth':
                return countMonth(data)
            case 'birdCount':
                return birdGraphCount(data, helperList)
            case 'locationCount':
                return locationCount(data, helperList)
            case 'sectorCount':
                return sectorCount(data, helperList)
            case 'omsCount':
                return omsCount(data, helperList)
            case 'topIncident':
                return incidentTypeCount(data, helperList)
            case 'subDamageLevel':
                return data ?? []
            case 'multiplierStats':
                return {
                    totalNotifications: Number(data?.totalNotifications) || 0,
                    totalMultiplier: Number(data?.totalMultiplier) || 0
                }

            default:
                return getLength(data)
        }
    }

    await ReportService.notificationCountGroupingByAny(
        countType === "countMonth",
        {
            parameter,
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState: subState || undefined,
            uUserType: uUserType || undefined
        }
    ).then( (promisseResponse: any) => {
            const finalizedBySubStatus = promisseResponse?.data?.finalizedBySubStatus
            const rawResponse = promisseResponse?.data?.response
            const payload = getCountType(countType, rawResponse, helperList, finalizedBySubStatus) || 0
            
            dispatch({
                type: action,
                payload: payload
            } as ReportAction)
        })
        .catch((error: AxiosError) => {
            dispatch({
                type: errorAction,
                payload: error.message
            } as ReportAction)
        })
        .finally(() => { })
}

const getAutoNotificationReport = async ({
    dispatch,
    action,
    errorAction,
    parameter = null,
    locations = null,
    reporterSector = null,
    startDate = null,
    finalDate = null,
    damageLevel = null,
    incidentType = null,
    omsClassification = null,
    status = null,
    sentinelEvent = null,
    imageRisk = null,
    sectorActive = null,
    subState = null,
    uUserType = null,
}: IReportProps) => {

    await ReportService.autoNotification({
        parameter,
        locations,
        reporterSector,
        startDate,
        finalDate,
        damageLevel,
        incidentType,
        omsClassification,
        bringArchived: true,
        status,
        sentinelEvent,
        imageRisk,
        sectorActive,
        subState: subState || undefined,
        uUserType: uUserType || undefined
    })
        .then((promisseResponse: any) => {
            dispatch({
                type: action,
                payload: promisseResponse?.data?.ratio || 0
            } as ReportAction)
        })
        .catch((error: AxiosError) => {
            dispatch({
                type: errorAction,
                payload: error.message
            } as ReportAction)
        })
        .finally(() => { })
}

const getDateDifference = async ({
    dispatch,
    action,
    errorAction,
    parameter = null,
    locations = null,
    reporterSector = null,
    startDate = null,
    finalDate = null,
    damageLevel = null,
    incidentType = null,
    omsClassification = null,
    status = null,
    sentinelEvent = null,
    imageRisk = null,
    sectorActive = null,
    uUserType = null,
}: IReportProps) => {

    await ReportService.avgDate({
        parameter,
        locations,
        reporterSector,
        startDate,
        finalDate,
        damageLevel,
        incidentType,
        omsClassification,
        bringArchived: true,
        status,
        sentinelEvent,
        imageRisk,
        sectorActive,
        uUserType: uUserType || undefined
    })
        .then((promisseResponse: any) => {
            let totalSeconds = 0;
            const dateIncident = new Date(promisseResponse?.data?.incidentDate);
            const sysCreatedOn = new Date(promisseResponse?.data?.createdDate);
            const timeDifference = sysCreatedOn.getTime() - dateIncident.getTime();
            if (!isNaN(timeDifference)) {
                totalSeconds = totalSeconds + timeDifference;
            }
            const obj = {
                totalSeconds: totalSeconds / 1000,
                totalNotifications: 0,
            };
            dispatch({
                type: action,
                payload: obj
            } as ReportAction)
        })
        .catch((error: AxiosError) => {
            dispatch({
                type: errorAction,
                payload: error.message
            } as ReportAction)
        })
        .finally(() => { })
}

const getDownloadExcelReport =async ({
    dispatch,
    errorAction,
    locations = null,
    reporterSector = null,
    startDate = null,
    finalDate = null,
    damageLevel = null,
    incidentType = null,
    omsClassification = null,
    status = null,
    sentinelEvent = null,
    imageRisk = null,
    sectorActive = null,
    uUserType = null,
}: IReportProps) => {

   await ReportService.downloadExcel({
        locations,
        reporterSector,
        startDate,
        finalDate,
        damageLevel,
        incidentType,
        omsClassification,
        status,
        sentinelEvent,
        imageRisk,
        sectorActive,
        uUserType: uUserType || undefined
    })
        .then((promisseResponse: any) => {
            return true
         })
        .catch((error: AxiosError) => {
            dispatch({
                type: errorAction,
                payload: error.message
            } as ReportAction)
            
        })
        .finally(() => { })
}

const ReportActions = {
    getSectorsReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_SECTORS_WITH_NOTIFICATION_COUNT,
            errorAction: REDUX.reducers.ERROR_SECTORS,
            countType: 'length',
            parameter: "incident_sector",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getUnityReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_LOCATIONS_WITH_NOTICATION_COUNT,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'length',
            parameter: "location_incident",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getStatusReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_COUNT_ALL_NOTIFICATIONS_BY_STATUS,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'status',
            parameter: "u_state",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getSubDamageLevelReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_NOTIFICATION_COUNT_BY_SUB_DAMAGE_LEVEL,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'subDamageLevel',
            parameter: "u_sub_damage_level",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getMultiplierStatsReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_MULTIPLIER_STATS,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'multiplierStats',
            parameter: "multiplier_stats",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getPerUnityReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_NOTICATIONS_PER_LOCATION,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'locationCount',
            parameter: "location_incident",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getMonthReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_NOTICATIONS_PER_MONTH,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'countMonth',
            parameter: "u_notification_creation_month;u_notification_creation_year",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            bringArchived: true,
            omsClassification,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getBirdGraphReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_NOTICATIONS_PER_DAMAGE,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'birdCount',
            parameter: "damage_level",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getOmsCountReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_NOTICATIONS_PER_OMS,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'omsCount',
            parameter: "u_oms_classification",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getTopIncidentTypeReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        sectorActive = null,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        getReport({
            dispatch,
            action: REDUX.reducers.GET_MOST_RECLASSIFIED_INCIDENT_TYPE,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'topIncident',
            parameter: "incident_type",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            subState,
            uUserType
        })
    },
    getAutoNotificationReport: async ({
        dispatch,
        parameter = null,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        getAutoNotificationReport({
            dispatch,
            action: REDUX.reducers.GET_AUTO_NOTIFICATION,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            parameter,
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            subState,
            uUserType
        })
    },
    getDateDifferenceReport: async ({
        dispatch,
        parameter = null,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        uUserType = null,
    }: IReportProps) => {
        getDateDifference({
            dispatch,
            action: REDUX.reducers.ALL_USER_NOTIFICATION_STATISTICS,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            parameter: 'sys_created_on;date_incident',
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            uUserType
        })
    },
    getPerSectorReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_NOTICATIONS_PER_SECTOR,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'sectorCount',
            parameter: "reporter_sector",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            subState,
            uUserType
        })
    },
    getPerNotificationSectorReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        helperList,
        subState = null,
        uUserType = null,
    }: IReportProps) => {
        await getReport({
            dispatch,
            action: REDUX.reducers.GET_ALL_NOTICATIONS_PER_INCIDENT_SECTOR,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            countType: 'sectorCount',
            parameter: "incident_sector",
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            helperList,
            status,
            sentinelEvent,
            imageRisk,
            subState,
            uUserType
        })
    },
    getDownloadExcelReport: async ({
        dispatch,
        locations = null,
        reporterSector = null,
        startDate = null,
        finalDate = null,
        damageLevel = null,
        incidentType = null,
        omsClassification = null,
        status = null,
        sentinelEvent = null,
        imageRisk = null,
        sectorActive = null,
        uUserType = null,
    }: IReportProps) => {
        getDownloadExcelReport({
            dispatch,
            errorAction: REDUX.reducers.ERROR_NOTIFICATIONS,
            locations,
            reporterSector,
            startDate,
            finalDate,
            damageLevel,
            incidentType,
            omsClassification,
            bringArchived: true,
            status,
            sentinelEvent,
            imageRisk,
            sectorActive,
            uUserType
        })
    },

}
export default ReportActions
